import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import {
  ERC20TokenSymbol,
  getPayablePoolTokensWithoutNative,
} from '@ui/constants/tokens';
import { useDataChainId } from '@ui/hooks/datachain';
import { toReadableValue } from '@ui/lib/bignumber';
import {
  FALLBACK_ADDRESS,
  getContractAddress,
} from '@ui/services/contract/constants';
import { Address } from '@ui/types/alias';
import { TxnMutateCallBackParams } from '@ui/types/tx';
import { Nullable, PossibleBigNumber } from '@ui/types/utility';
import BigNumber from 'bignumber.js';
import { isNil } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { erc20Abi, erc20Abi_bytes32, maxUint256, zeroAddress } from 'viem';
import { useAccount, useChainId, useReadContract } from 'wagmi';

import { useMultiCall } from './useMultiCall';
import { useTxnWriteContract } from './useTxnWriteContract';

export const useErc20Balance = ({
  account,
  tokenAddress,
  decimals,
  refetchInterval,
  enabled = true,
}: {
  tokenAddress: Nullable<Address>;
  account: Nullable<Address>;
  decimals: Nullable<number>;
  enabled?: boolean;
  refetchInterval?: number;
}) => {
  return useReadContract({
    abi: erc20Abi,
    address: tokenAddress as Address,
    functionName: 'balanceOf',
    args: [account as Address],
    chainId: useDataChainId(),
    scopeKey: 'useErc20Balance',
    query: {
      refetchInterval: refetchInterval,
      enabled: !!tokenAddress && !!account && !!decimals && enabled,
      select: (balance) => {
        if (isNil(balance) || isNil(decimals)) return undefined;

        return toReadableValue(balance, decimals);
      },
    },
  });
};

export const useErc20TotalSupply = ({
  tokenAddress,
  decimals,
}: {
  tokenAddress?: Address;
  decimals?: number;
}) => {
  return useReadContract({
    abi: erc20Abi_bytes32,
    address: tokenAddress as Address,
    functionName: 'totalSupply',
    args: [],
    chainId: useDataChainId(),
    query: {
      enabled: !!tokenAddress,
      select: (totalSupply) => {
        if (isNil(totalSupply) || isNil(decimals)) return undefined;

        return toReadableValue(totalSupply, decimals);
      },
    },
  });
};

const useTokenAllowance = ({
  tokenAddress,
  owner,
  spender,
  enabled,
  tokenDecimals,
}: {
  tokenDecimals: number | undefined;
  owner: Address;
  tokenAddress: Address;
  spender: Address;
  enabled: boolean;
}) => {
  const chainId = useDataChainId();

  return useReadContract({
    abi: erc20Abi_bytes32,
    address: tokenAddress,
    functionName: 'allowance',
    args: [owner, spender],
    chainId: chainId,
    query: {
      enabled: !!tokenDecimals && enabled,
      select: (data) =>
        isNil(tokenDecimals) ? undefined : toReadableValue(data, tokenDecimals),
    },
  });
};

export const useTokenDecimals = ({
  tokenAddress,
}: {
  tokenAddress: Address | undefined;
}) => {
  const chainId = useDataChainId();

  return useReadContract({
    abi: erc20Abi_bytes32,
    address: tokenAddress,
    functionName: 'decimals',
    args: [],
    chainId: chainId,
    query: {
      enabled: !!tokenAddress,
    },
  });
};

export type UseIsNeedTokenApprovalParam = {
  tokenAddress: Address | undefined;
  spender?: Address;
  requiredAmount: PossibleBigNumber;
};
export const useIsNeedTokenApproval = ({
  tokenAddress,
  spender,
  requiredAmount,
}: UseIsNeedTokenApprovalParam) => {
  const chainId = useDataChainId();
  const { address: owner } = useAccount();
  const leverUpAddress = getContractAddress('LeverUp', chainId);
  const { data: tokenDecimals } = useTokenDecimals({ tokenAddress });

  const { data: allowance, ...rest } = useTokenAllowance({
    owner: owner ?? FALLBACK_ADDRESS,
    tokenAddress: tokenAddress ?? FALLBACK_ADDRESS,
    spender: spender ? spender : leverUpAddress ?? FALLBACK_ADDRESS,
    tokenDecimals,
    enabled:
      !!tokenAddress &&
      !!owner &&
      (!!leverUpAddress || !!spender) &&
      !!tokenDecimals,
  });

  const isNeedTokenApproval = useMemo(() => {
    if (tokenAddress == zeroAddress) {
      return false;
    }
    if (isNil(allowance)) return true;

    return allowance.lt(requiredAmount ?? BigNumber(0));
  }, [allowance, requiredAmount, tokenAddress]);

  return {
    data: isNeedTokenApproval,
    ...rest,
    isLoading: rest.isLoading && rest.fetchStatus !== 'idle',
  };
};

export const useApproveErc20Token = ({
  tokenAddress,
  spenderAddress,
  onSuccess,
  onError,
  onSettled,
  onMutate,
}: {
  tokenAddress: Address | undefined;
  spenderAddress?: Address | undefined;
} & TxnMutateCallBackParams) => {
  const chainId = useDataChainId();

  const { data, writeContract, isPending, writeContractAsync } =
    useTxnWriteContract({
      onSuccess,
      onError,
      onSettled,
      onMutate,
    });

  const write = useCallback(() => {
    const leverUpAddress = getContractAddress('LeverUp', chainId);
    if (isNil(leverUpAddress) || isNil(tokenAddress)) return;

    return writeContract({
      abi: erc20Abi_bytes32,
      address: tokenAddress,
      functionName: 'approve',
      args: [spenderAddress ? spenderAddress : leverUpAddress, maxUint256],
    });
  }, [chainId, spenderAddress, tokenAddress, writeContract]);
  const writeAsync = useCallback(() => {
    const leverUpAddress = getContractAddress('LeverUp', chainId);
    if (isNil(leverUpAddress) || isNil(tokenAddress)) return;

    return writeContractAsync({
      abi: erc20Abi_bytes32,
      address: tokenAddress,
      functionName: 'approve',
      args: [spenderAddress ? spenderAddress : leverUpAddress, maxUint256],
    });
  }, [chainId, spenderAddress, tokenAddress, writeContractAsync]);

  return useMemo(() => {
    return {
      data,
      writeContract: write,
      writeContractAsync: writeAsync,
      isPending,
    };
  }, [data, isPending, write, writeAsync]);
};

export const useErc20Symbol = ({
  tokenAddress,
}: {
  tokenAddress: Nullable<Address>;
}) => {
  return useReadContract({
    abi: erc20Abi, // for this get symbol, use erc20Abi can work, but erc20Abi_bytes32 can not
    address: tokenAddress as Address,
    functionName: 'symbol',
    args: [],
    chainId: useDataChainId(),
    query: {
      enabled: !!tokenAddress,
    },
  });
};

type Erc20BalanceMap = Record<ERC20TokenSymbol, Nullable<BigNumber>>;
export const useErc20BalanceBatch = <T = Erc20BalanceMap>({
  account,
  queryOpts,
}: {
  account: Nullable<Address>;
  queryOpts?: Omit<
    UseQueryOptions<Erc20BalanceMap, Error, T>,
    'queryKey' | 'queryFn' | 'enabled'
  >;
}) => {
  const chainId = useChainId();
  const multicall = useMultiCall();

  return useQuery({
    ...queryOpts,
    enabled: !!multicall && !!account,
    queryKey: [useErc20BalanceBatchRootKey, multicall, account, chainId],
    queryFn: async () => {
      if (!multicall || !account) {
        throw 'should not happen';
      }
      // const lp = getErc20TokenBySymbol('LP', chainId);
      const erc20Tokens = [
        ...getPayablePoolTokensWithoutNative(chainId).filter(
          (v) => !v.isNative,
        ),
        // ...(lp ? [lp] : []),
      ];

      const res = await multicall({
        contracts: erc20Tokens.map((t) => {
          return {
            abi: erc20Abi,
            address: t.address,
            functionName: 'balanceOf',
            args: [account],
          };
        }),
      });

      // Initialize balance map with all possible ERC20TokenSymbol values
      const balanceMap: Erc20BalanceMap = {
        USDC: null,
        WMON: null,
        WRAPPED_NATIVE: null,
        LVUSD: null,
        LVMON: null,
        sLVMON: null,
        LV: null,
        Rona: null,
        EMO: null,
        xLV: null,
        yLV: null,
        sxLV: null,
        // LP: null,
      };

      // Update with actual fetched balances
      erc20Tokens.forEach((t, index) => {
        if (res[index].result && t.symbol in balanceMap) {
          balanceMap[t.symbol as keyof Erc20BalanceMap] = toReadableValue(
            res[index].result as bigint,
            t.decimals,
          );
        }
      });

      return balanceMap;
    },
  });
};
const useErc20BalanceBatchRootKey = 'useErc20BalanceBatch';

useErc20BalanceBatch.rootKey = useErc20BalanceBatchRootKey;
